import useReturnRequestsWithScan from "@babackapp/private-api-client/client/use-get-return-request-with-scan";
import { useFormErrorNotification } from "@babackapp/react-hook-forms-components/form-notifications";
import useTranslation from "@babackapp/translations/use-translation";
import onScan from "onscan.js";
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { decodeScanKey } from "./decode-scan-key";

export default function useScannerMode(props: {
  shopId: string;
  active: boolean;
  runInterceptors?: (code: string) => boolean;
}) {
  const [scanInitiated, setScanInitiated] = useState(false);
  const [searchCode, setSearchCode] = useState("");
  const t = useTranslation();
  const runInterceptorsRef = useRef(props.runInterceptors);

  const { data, error, isPending, isSuccess } = useReturnRequestsWithScan({
    shopId: props.shopId,
    search: searchCode,
  });
  const navigate = useNavigate();
  const errorNotification = useFormErrorNotification({
    title: t("scannerMode.errorNotification.title"),
    message: t("scannerMode.errorNotification.message"),
  });
  const notFoundNotification = useFormErrorNotification({
    title: t("scannerMode.notFoundNotification.title"),
    message: t("scannerMode.notFoundNotification.message"),
  });

  useEffect(() => {
    runInterceptorsRef.current = props.runInterceptors;
  });

  /**
   * A code that matches nothing leaves `data` empty, so watching `data` alone
   * never re-ran this effect: the operator got no feedback whatsoever and the
   * scan simply looked like it did nothing.
   *
   * `isSuccess` is what tells a resolved-but-empty lookup (the endpoint answers
   * 200 with no body when no return carries the code) apart from a query that
   * has not run yet, and `error` covers the request failing outright. The two
   * get distinct notifications: an unknown code is the warehouse's own doing —
   * a carrier label, an article barcode, a parcel from another shop — while a
   * failed request is ours and worth retrying.
   *
   * Resetting `searchCode` disables the query, which brings the state back to
   * pending and re-runs the effect: the guard below stops that second pass from
   * notifying twice.
   */
  useEffect(() => {
    if (!isSuccess && !error) return;

    if (data) {
      navigate(`/shops/${props.shopId}/returns/${data.id}`);
    } else if (error) {
      errorNotification();
    } else {
      notFoundNotification();
    }
    setSearchCode("");
  }, [data, error, isSuccess]);

  useEffect(() => {
    if (props.active) {
      setScanInitiated(true);
      if (!onScan.isAttachedTo(document)) {
        onScan.attachTo(document, {
          suffixKeyCodes: [13], // enter-key expected at the end of a scan
          reactToPaste: true, // Compatibility to built-in scanners in paste-mode (as opposed to keyboard-mode)
          // Keeps the characters the default decoder drops, `-` above all.
          keyCodeMapper: decodeScanKey,
          onScan: function (sCode: string) {
            const code = sCode.trim();
            if (!code) return;

            if (!runInterceptorsRef.current?.(code)) {
              setSearchCode(code);
            }
          },
          onKeyDetect: function () {
            // output all potentially relevant key events - great for debugging!
          },
        });
      }
    } else if (scanInitiated) {
      if (onScan.isAttachedTo(document)) {
        onScan.detachFrom(document);
      }
    }
  }, [props.active]);

  return { data, error, isPending };
}
